/**
 * What this session CHANGED, folded out of its own transcript. In mod-core
 * (from the window's thread feature) so a closing page can fold it into the
 * page seed (RFC 0360 S2): the live page's transcript is a tail, and the
 * header's changed count has to stay exact over the whole chain.
 *
 * The question is "what did this session actually touch", and the log already
 * answers it: an agent edits a file or writes a document by CALLING A TOOL, and
 * the call carries the path, while the result carries the id of anything it
 * created. Nothing new is stored. Deleting the transcript deletes the answer,
 * which is correct, because the answer was only ever a reading of it.
 *
 * NOT `mod.session_change`. RFC 0235 defines exactly this linkage as a log event
 * (sessionId + vcsCommit + files) and mod-core has the writer for it. Nothing in
 * production calls it, so there is not one such event in any project, and it
 * would only ever reach COMMIT granularity anyway. Tool calls are recorded today,
 * for every session, at the granularity of the individual edit. The same reading
 * the Review canvas does for session blame (`sessionEdits.js`), one realm over.
 *
 * RECOGNISED BY SHAPE, NOT BY A TABLE OF NAMES, for files: tools arrive from MCP
 * servers and from apps this file has never heard of, and a fixed list would
 * silently drop every one of them. `editDiff.ts` and `toolSummary.ts` take the
 * same line for the same reason.
 *
 * BY NAME SUFFIX for documents, because there is no shape to go on: a document
 * write is a `documentId` and some text, which is also what half the other tools
 * carry. The three realms prefix the name differently (`update_document` in the
 * cli, `mcp__mod__update_document` as injected, the bridge's own id in a
 * sandbox), so the suffix is what they agree on. RFC 0297 §4a rejects this
 * reading for a DURABLE projection and appends `mod.touched` instead; it is
 * accepted here because this is a strip that can be wrong by omission, not a
 * record anything else is built on.
 *
 * A FAILED CALL CHANGED NOTHING. A `tool_result` marked `isError` is dropped, so
 * an edit that could not find its string does not put a file on this list.
 */

import type { TranscriptMessage } from './projection-transcript'
import { type WrittenOn, writtenPaths } from './writes'

/** A string argument, whatever it is wrapped in (a plain string in Node, an
 *  `ImmutableString` or `{val}` off the log in the app). */
function argString(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  if (value !== null && typeof value === 'object') {
    const inner = (value as { val?: unknown }).val
    if (typeof inner === 'string') return inner
  }
  return undefined
}

/** Whether the session brought the thing into being, or found it there. */
export type ChangeKind = 'created' | 'edited'

export interface FileChange {
  /** The path the tool named, in full. Absolute, as tools write them. */
  path: string
  /** The last two segments, for a row that has to stay one line wide. */
  label: string
  kind: ChangeKind
  /** How many calls touched it. A file edited eleven times reads differently
   *  from one touched once, and the count is the only part of that this can
   *  honestly say. */
  touches: number
  /** Where the LAST write to it landed (RFC 0329 3a), as recorded on that
   *  write's result. Absent when nobody placed it. */
  writtenOn?: WrittenOn
}

export interface DocumentChange {
  /** Present once it is known: a create call carries no id in its ARGUMENTS
   *  (the id is minted by the write), so it is read back out of the result. An
   *  absent id means the row can be named but not opened. */
  documentId?: string
  /** The best name the call itself gave — a title or a path. The catalog's
   *  title is better and the renderer prefers it; this is what is left when the
   *  document is not in the catalog this window can see. */
  label: string
  kind: ChangeKind
  touches: number
}

/** One write to one file, when it happened and how big it was (RFC 0313
 *  3h): the row a session's Changes draws, in time order beside its document
 *  changes. `files` above is the same writes folded per file. */
export interface FileEdit {
  /** The call it came from, and the path; together unique. */
  key: string
  path: string
  writtenOn?: WrittenOn
  /** Millis, off the log entry. Absent on a transcript from before 0313. */
  at?: number
  insertions: number
  deletions: number
}

export interface SessionChanges {
  files: FileChange[]
  documents: DocumentChange[]
  /** Every file write, oldest first. */
  edits: FileEdit[]
}

const lineCount = (text: string): number =>
  text ? text.replace(/\n$/, '').split('\n').length : 0

/** Lines one call added and removed in one file, read off what went in: a
 *  diff where the tool sent one, else the old and new text. */
function editSize(
  args: unknown,
  path: string,
): { insertions: number; deletions: number } {
  const a = record(args) ?? {}
  const entries: Record<string, unknown>[] = []
  for (const key of ['changes', 'edits', 'replacements', 'operations']) {
    const list = a[key]
    if (Array.isArray(list))
      for (const e of list) {
        const r = record(e)
        if (!r) continue
        const p = firstKey(r, ['path', 'file_path', 'filePath'])
        if (!p || p === path) entries.push(r)
      }
  }
  if (!entries.length) entries.push(a)
  let insertions = 0
  let deletions = 0
  for (const e of entries) {
    if (typeof e.insertions === 'number' && typeof e.deletions === 'number') {
      insertions += e.insertions
      deletions += e.deletions
      continue
    }
    const diff = str(e.diff)
    if (diff) {
      for (const line of diff.split('\n')) {
        if (line.startsWith('+++') || line.startsWith('---')) continue
        if (line.startsWith('+')) insertions++
        else if (line.startsWith('-')) deletions++
      }
      continue
    }
    const added = firstKey(e, [
      'new_string',
      'newString',
      'new_str',
      'new_text',
    ])
    const removed = firstKey(e, [
      'old_string',
      'oldString',
      'old_str',
      'old_text',
    ])
    const body = firstKey(e, ['content', 'contents'])
    insertions += lineCount(added || body)
    deletions += lineCount(removed)
  }
  return { insertions, deletions }
}

/** A tool whose name claims it brings a file into being rather than amends one. */
const CREATES = /write|create|overwrite/i
/**
 * A string argument, whatever it is wrapped in, or '' when there is none.
 *
 * The unwrapping moved to `toolArgs`: three readers of tool arguments had each
 * grown their own copy of it, and the one that did NOT have it rendered every
 * edit as raw JSON. `argString` is the single answer to "is this argument
 * textual"; this keeps only the '' default, which is what the callers below
 * want and what `argString` deliberately does not assume.
 */
function str(value: unknown): string {
  return argString(value) ?? ''
}

function record(args: unknown): Record<string, unknown> | null {
  if (!args || typeof args !== 'object' || Array.isArray(args)) return null
  return args as Record<string, unknown>
}

function firstKey(args: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = str(args[key])
    if (value) return value
  }
  return ''
}

/**
 * The document verb a call is, or ''.
 *
 * Matched on the last segment of the name so the same call reads the same in
 * every realm. `delete_document` is deliberately absent: a document that is
 * gone is not something a person can be shown, and listing it under "changed"
 * would offer a row that cannot open.
 */
function documentVerb(name: string): 'create' | 'update' | '' {
  const tail = name.split(/__|\./).pop() ?? name
  if (tail === 'create_document') return 'create'
  if (
    tail === 'update_document' ||
    tail === 'patch_document' ||
    tail === 'write_document'
  )
    return 'update'
  return ''
}

/** The id a create call minted, read out of its result line. */
const CREATED_ID = /Created (?:document|instance)\s+(\S+)/i

/**
 * Fold a transcript into what it changed.
 *
 * Ordered MOST RECENTLY TOUCHED FIRST, because the last thing a session did is
 * the thing being read about. Ordering by path would sort by where the code
 * happens to live, which is a fact about the repository and not about the
 * session.
 */
export function changesFromTranscript(
  transcript: readonly TranscriptMessage[],
): SessionChanges {
  /** Results by call id, so a call can be dropped when it failed and a created
   *  id can be read back off the write that minted it. */
  const results = new Map<
    string,
    { text: string; isError: boolean; writtenOn?: WrittenOn }
  >()
  for (const message of transcript) {
    if (message.role !== 'tool_result' || !message.toolUseId) continue
    const text = message.content
      .filter((b) => b.type === 'text')
      .map((b) => str((b as { text?: unknown }).text))
      .join('\n')
    // Read loosely: a core build from before RFC 0329 has no `landed`.
    const writtenOn = (message as { writtenOn?: WrittenOn }).writtenOn
    results.set(message.toolUseId, {
      text,
      isError: message.isError === true,
      ...(writtenOn ? { writtenOn } : {}),
    })
  }

  const files = new Map<string, FileChange & { order: number }>()
  const edits: FileEdit[] = []
  const documents = new Map<string, DocumentChange & { order: number }>()
  let order = 0

  for (const message of transcript) {
    if (message.role !== 'assistant') continue
    for (const block of message.content) {
      if (block.type !== 'tool_use') continue
      const result = results.get(block.id)
      // A call still in flight has no result yet, and IS counted: the write is
      // in progress, and a strip that waited for the result would flicker a
      // file in only after the agent had moved on from it. Only a call that
      // came back an error is dropped.
      if (result?.isError) continue
      const name = str(block.name)
      const seq = order++

      // A document tool is never a file write, even though create_document
      // carries a `path`: that is the document's place in the catalog, not a
      // file on disk, and reading it as one listed every document twice.
      const paths = documentVerb(name)
        ? []
        : writtenPaths(name, block.arguments)
      // The result records where the FIRST file landed. A call that wrote
      // several (Codex's fileChange) wrote them in one checkout, so the rest
      // are placed against its root.
      const first = result?.writtenOn
      const root =
        first && paths[0]?.endsWith(first.relPath)
          ? paths[0].slice(0, paths[0].length - first.relPath.length)
          : null
      for (const path of paths) {
        const writtenOn: WrittenOn | undefined =
          path === paths[0]
            ? first
            : first && root && path.startsWith(root)
              ? { ...first, relPath: path.slice(root.length) }
              : undefined
        edits.push({
          key: `${block.id}:${path}`,
          path,
          ...(writtenOn ? { writtenOn } : {}),
          ...(message.at !== undefined ? { at: message.at } : {}),
          ...editSize(block.arguments, path),
        })
        const existing = files.get(path)
        if (existing) {
          existing.touches += 1
          existing.order = seq
          // The last write decides, as the order does.
          if (writtenOn) existing.writtenOn = writtenOn
          else delete existing.writtenOn
        } else {
          files.set(path, {
            ...(writtenOn ? { writtenOn } : {}),
            path,
            label: lastSegments(path),
            // The FIRST call decides. A file a session wrote whole and then
            // amended twice was created by this session; one it edited and
            // later rewrote was not.
            kind: CREATES.test(name) ? 'created' : 'edited',
            touches: 1,
            order: seq,
          })
        }
      }
      if (paths.length) continue

      const verb = documentVerb(name)
      if (!verb) continue
      const a = record(block.arguments) ?? {}
      const id =
        firstKey(a, ['documentId', 'document_id']) ||
        (verb === 'create'
          ? (CREATED_ID.exec(result?.text ?? '')?.[1] ?? '')
          : '')
      const label = firstKey(a, ['title', 'path', 'name']) || id || 'document'
      // Keyed by id where there is one, and by label where there is not, so a
      // create whose result has not arrived does not merge with an unrelated
      // untitled write.
      const key = id || `label:${label}`
      const existing = documents.get(key)
      if (existing) {
        existing.touches += 1
        existing.order = seq
        if (!existing.documentId && id) existing.documentId = id
      } else {
        documents.set(key, {
          ...(id ? { documentId: id } : {}),
          label,
          kind: verb === 'create' ? 'created' : 'edited',
          touches: 1,
          order: seq,
        })
      }
    }
  }

  const recentFirst = <T extends { order: number }>(rows: T[]): T[] =>
    rows.sort((x, y) => y.order - x.order)

  return {
    files: recentFirst([...files.values()]).map(({ order: _o, ...row }) => row),
    documents: recentFirst([...documents.values()]).map(
      ({ order: _o, ...row }) => row,
    ),
    edits,
  }
}

/** The last two segments of a path — enough to tell two `index.ts` apart. */
export function lastSegments(path: string, count = 2): string {
  const parts = path.split('/').filter(Boolean)
  return parts.slice(-count).join('/') || path
}

/**
 * Two folds over DISJOINT parts of one session joined, `earlier` before
 * `later`: what pages before this one changed (the seed) and what the live
 * page changed. A file both touched counts both sets of touches, keeps the
 * earlier `created`, and takes the later placement.
 */
export function mergeSessionChanges(
  earlier: SessionChanges | null | undefined,
  later: SessionChanges,
): SessionChanges {
  if (!earlier) return later
  const files = new Map<string, FileChange>()
  for (const f of [...earlier.files].reverse()) files.set(f.path, { ...f })
  const laterFiles: FileChange[] = []
  for (const f of later.files) {
    const e = files.get(f.path)
    if (e) {
      files.delete(f.path)
      laterFiles.push({
        ...f,
        kind: e.kind === 'created' ? 'created' : f.kind,
        touches: e.touches + f.touches,
      })
    } else laterFiles.push(f)
  }
  const documents = new Map<string, DocumentChange>()
  const docKey = (d: DocumentChange) => d.documentId ?? `label:${d.label}`
  for (const d of [...earlier.documents].reverse())
    documents.set(docKey(d), { ...d })
  const laterDocs: DocumentChange[] = []
  for (const d of later.documents) {
    const e = documents.get(docKey(d))
    if (e) {
      documents.delete(docKey(d))
      laterDocs.push({
        ...d,
        kind: e.kind === 'created' ? 'created' : d.kind,
        touches: e.touches + d.touches,
      })
    } else laterDocs.push(d)
  }
  return {
    files: [...laterFiles, ...[...files.values()].reverse()],
    documents: [...laterDocs, ...[...documents.values()].reverse()],
    edits: [...earlier.edits, ...later.edits],
  }
}
